/**
 * Print technical zones as Rust `shape` drawables, in absolute scene coordinates (print px).
 *
 * The geometry is a 1:1 port of the editor's Konva `BleedSafeZoneLayer` rect math
 * (libs/frontend/photobook/.../layers/bleed-safe-zone-layer.ts), `getBleedOffset`
 * (photobook-model spread-queries) and `computeCenterGuideGeometry` (canvas-engine) — the same
 * source of truth, so the Rust overlay aligns with the Konva zones. Units differ on purpose: rects are absolute
 * print px and the painter scales them by the view, the border's DASH is CSS px, and its WIDTH is DEVICE px. The
 * camera scales none of the latter two, so the mark keeps one weight and one rhythm at any zoom while what it
 * marks moves with the sheet.
 */

import { trimSheet } from '@lite-workspace/photobook-model';
import type { SceneFrameDecor } from './frame-decor.model';

/** Structural subset of PrintSizes / CoverPrintSizes. */
export interface ZonePrintSizes {
  readonly width: number;
  readonly height: number;
  readonly safe_horizontal: number;
  readonly safe_vertical: number;
  readonly cut_horizontal: number;
  readonly cut_vertical: number;
  readonly rasstav_horizontal?: number;
  readonly spine_width?: number;
  readonly wrap_horizontal?: number;
  readonly wrap_vertical?: number;
}

export interface ZoneRect {
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
}

export interface ZoneShape {
  type: 'shape';
  id: string;
  rect: ZoneRect;
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  /** Обводка кадра: линия уходит ВНУТРЬ rect, как у рамки с фото. Зоны и рамки выделения её не ставят. */
  strokeInside?: boolean;
  dash?: number[];
  blend: string;
  /** Draw-time affine (tiny-skia from_row) — rotates the selection/hover frame with the box. */
  transform?: number[];
  /** Selectable content drawn as a shape (empty image-frame placeholder); zones/frames stay non-interactive. */
  interactive?: boolean;
  /** Alpha mask, keyed like an image drawable's — a photo-less frame is a shape and must still show its mask. */
  maskRef?: string;
  /**
   * Where the mask's pixels lie when that is NOT this shape's rect: the rect of the layer the frame is clipped
   * to (Photoshop's clipping mask). The mask then stands still while the frame slides under it.
   */
  maskRect?: ZoneRect;
  /**
   * Frame layer effects + corner radius. An EMPTY photo frame draws as a shape, so its decoration has to
   * ride here too — otherwise a frame would gain its outline and shadow only once a photo landed in it.
   */
  decor?: SceneFrameDecor;
  locked?: boolean;
}

// --- Editor zone constants (originally ported from photobook .../layers/constants.ts; dash, widths, colors diverged) ---
/**
 * ONE dash for every zone border. Four different rhythms used to run side by side — [8,4] bleed, [6,4] cover
 * wrap, [8,6] safe, [10,6] spine — and where two of them met the eye read the mismatch as an irregular pattern
 * rather than as different zones. One rhythm does not make shared sides safe: two frames on the same segment
 * dash in opposite directions and fill each other's gaps (the расстав frame still shares its top and bottom
 * run with the bleed border). Screen px, never scaled by the camera: the dash keeps its size at any zoom,
 * so the border reads the same close up as it does on the whole spread. Kept fine to match the hairline weight
 * below — a coarser rhythm on a half-pixel line reads as a row of ticks rather than as a border.
 */
export const ZONE_BORDER_DASH = [3, 2];

/**
 * ONE weight for every zone border, and the camera never scales it. The spine used to be 3 against everyone
 * else's 1 AND grew with the zoom, so close in it read as a heavy bar rather than as the same kind of mark as its
 * neighbours. A zone border is a hairline that says "the paper ends here": what it marks moves with the sheet,
 * the mark itself does not.
 *
 * 🚨 DEVICE px, not CSS px — the painter divides by the layer's real backing ratio so this is exactly ONE physical
 * pixel on a 1×, 1.5× or 2× display. Naming it in CSS px is what broke: half a CSS px is one device pixel only on
 * a 2× screen, and on anything else it lands BELOW a pixel, where Canvas2D coverage-antialiases it into precisely
 * the translucent washed-out line this work exists to remove.
 */
export const ZONE_BORDER_DEVICE_PX = 1;

export const BLEED_ZONE_STROKE_COLOR = '#FF4444';
export const BLEED_ZONE_FILL_COLOR = 'rgba(250, 0, 0, 0.25)';

export const SAFE_ZONE_FILL_COLOR = 'rgba(76, 175, 80, 0.12)';
export const SAFE_ZONE_STROKE_COLOR = '#4CAF50';

export const SPINE_ZONE_FILL_COLOR = 'rgba(255, 152, 0, 0.28)';
export const SPINE_ZONE_STROKE_COLOR = '#FB8C00';

// Расстав is purple everywhere: in the safe green it read as the safe zone. Шарнир — verification export only.
export const RASSTAV_ZONE_STROKE_COLOR = '#9C27B0';
export const RASSTAV_ZONE_FILL_COLOR = 'rgba(156, 39, 176, 0.12)';

export const HINGE_ZONE_STROKE_COLOR = '#009688';

// Загиб на схеме своего продукта — жёлтый (решение владельца): рядом с красной обрезкой разворота он
// обязан читаться как другая зона. Редактор загиб по-прежнему красит как обрезку, эти константы — для схем.
export const WRAP_ZONE_STROKE_COLOR = '#FFC107';
export const WRAP_ZONE_FILL_COLOR = 'rgba(255, 193, 7, 0.14)';
export const HINGE_ZONE_FILL_COLOR = 'rgba(0, 150, 136, 0.12)';

// Направляющие ИСХОДНИКА — не зона продукта, а разметка автора макета, прочитанная из PSD. Цвет взят
// у направляющих Photoshop и потому теме не подчиняется: дизайнер сверяет линию на нашем холсте с той
// же линией в своём файле, и совпадение цвета — половина этой сверки.
export const AUTHORED_GUIDE_STROKE_COLOR = '#00ffff';

export const SELECTION_STROKE_COLOR = '#7177D6';
export const SELECTION_STROKE_WIDTH = 2;

// Canvas cannot read CSS custom properties, so --acc is mirrored ONCE here and every accent-tinted piece of
// photo chrome derives from it — four hand-written spellings would drift the moment the token moves.
export const ACCENT_COLOR = '#696ec5';
const ACCENT_RGB = '105, 110, 197';
export function accentAlpha(alpha: number): string {
  return `rgba(${ACCENT_RGB}, ${alpha})`;
}

// A selected PHOTO frame is ONE accent stroke drawn INSIDE its box, hard against the photo edge. Inside,
// because a frame on a bleed photo has nothing outside the sheet to be drawn on: an outward band there lands
// off the shown page, is eaten by the chrome clip, and the frame reads white instead of ours. Hard against the
// edge, because a lighter band between the picture and its frame reads as an unfilled margin, not as chrome.
// CSS px — the caller converts to buffer px so the frame keeps one on-screen width at any zoom.
/** ds-theme token, not a literal: a 2D context can't read a CSS var, so the painter resolves it per draw. */
export const PHOTO_SELECTION_COLOR_TOKEN = '--acc';
export const PHOTO_SELECTION_WIDTH = 2;

// Snap guides and the equal-gap ticks are drawn ON THE SPREAD, which is always light paper — so they take the
// on-light ink, the token that stays the same in both themes. The app's own --ink flips to near-white on the
// dark shell and would leave the guides invisible over the page.
export const SNAP_GUIDE_TOKEN = '--ink-on-light';

// Warn frame for a selected text box whose text overflows its rect (mirrors --bad in ds-theme.css).
export const SELECTION_OVERFLOW_STROKE_COLOR = '#d23f3a';
export const SELECTION_OVERFLOW_STROKE_WIDTH = 3;

export const HOVER_STROKE_COLOR = '#7177D6';
export const HOVER_STROKE_WIDTH = 1;

// CSS px of head-room the chrome region keeps outside the spread, so a selection frame on an edge-flush box
// draws whole instead of losing its outer half to the spread bounds.
export const CHROME_BLEED_PX = 8;

// The fade keeps a pointer sweeping across a spread from strobing frame to frame.
export const HOVER_FADE_MS = 140;

const DROP_HOVER_STROKE_COLOR = '#B3B7FF';
const DROP_HOVER_STROKE_WIDTH = 3;
// A refusal must read at a glance over any photo, so it is a heavy wash — far denser than the allow tint.
const DROP_FORBID_FILL_ALPHA = 0.72;

// The host resolves its token opaque (the canvas cannot read CSS vars); the wash alpha is this overlay's own
// constant. `#rrggbbaa` is the engine's own alpha spelling (engine/psd-render/core/src/color.rs), so the tint
// never has to duplicate a colour the stroke already names.
function washed(color: string, alpha: number): string {
  const hexPairs = /^#([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i.exec(color)?.slice(1);
  // Only INTEGER rgb() channels survive the fallback: a fractional or exotic colour must fall through to the
  // raw colour untouched — a forged half-hex string paints nothing at all.
  const intChannels = hexPairs
    ? undefined
    : /^rgba?\(\s*(\d{1,3})[,\s]+(\d{1,3})[,\s]+(\d{1,3})/i
        .exec(color)
        ?.slice(1)
        .map(part => Number(part).toString(16).padStart(2, '0'));
  const channels = hexPairs ?? intChannels;
  if (!channels || channels.length < 3) return color;
  const alphaPair = Math.round(alpha * 255)
    .toString(16)
    .padStart(2, '0');
  return `#${channels.join('')}${alphaPair}`.toLowerCase();
}

const DROP_HOVER_FILL_COLOR = washed(DROP_HOVER_STROKE_COLOR, 0.42);

type SelRect = { x: number; y: number; width: number; height: number; rotation?: number; transform?: number[] };

// Affine (text) wins; otherwise honor rect.rotation so the frame rotates for rotated objects/images too.
function selRect(r: SelRect): ZoneRect {
  const rr = rect(r.x, r.y, r.width, r.height);
  if (!r.transform) rr.rotation = r.rotation ?? 0;
  return rr;
}

/** Stroke-only `shape` outlining a selected drawable; rotates with the box (affine or rect.rotation). */
export function buildSelectionShape(r: SelRect, id = 'selection-box'): ZoneShape {
  const s = borderShape(id, selRect(r), SELECTION_STROKE_COLOR, SELECTION_STROKE_WIDTH, []);
  if (r.transform) s.transform = r.transform;
  return s;
}

/** Stroke-only `shape` outlining a hovered drawable; rotates with the box (affine or rect.rotation). */
export function buildHoverShape(r: SelRect): ZoneShape {
  const s = borderShape('hover-box', selRect(r), HOVER_STROKE_COLOR, HOVER_STROKE_WIDTH, []);
  if (r.transform) s.transform = r.transform;
  return s;
}

/** `forbidColor` (host-resolved token) paints the refusal variant instead of the accept tint. */
export function buildDropHoverShape(r: SelRect, forbidColor?: string): ZoneShape {
  const s: ZoneShape = {
    type: 'shape',
    id: 'drop-hover-box',
    rect: selRect(r),
    fill: forbidColor ? washed(forbidColor, DROP_FORBID_FILL_ALPHA) : DROP_HOVER_FILL_COLOR,
    stroke: forbidColor ?? DROP_HOVER_STROKE_COLOR,
    strokeWidth: DROP_HOVER_STROKE_WIDTH,
    blend: 'normal',
  };
  if (r.transform) s.transform = r.transform;
  return s;
}

/**
 * Per-zone visibility, mirroring the editor's five independent toggles (Зона обрезки / Безопасная /
 * Заворот / Корешок / Расстав). Omitted flags default to visible (back-compat with all-zones callers).
 * `bleed` governs the outer zone on spreads; `wrap` governs it on covers (same shapes, same color — only the
 * visibility toggle differs).
 */
export interface ZoneVisibility {
  bleed?: boolean;
  safe?: boolean;
  spine?: boolean;
  rasstav?: boolean;
  wrap?: boolean;
}

/** photobook = one safe frame spanning the trim; designEditor = two per-page safe frames split at the spine. */
export type ZoneStyle = 'photobook' | 'designEditor';

function visible(flag: boolean | undefined): boolean {
  return flag !== false;
}

/** Обложка отличается от разворота наличием корешка — единственный признак, других полей у неё нет. */
export function isCover(ps: ZonePrintSizes): boolean {
  return ps.spine_width != null;
}

/** Port of getBleedOffset: cover uses wrap_*; spread uses cut_*. */
export function bleedOffset(ps: ZonePrintSizes): { x: number; y: number } {
  if (isCover(ps)) {
    return { x: ps.wrap_horizontal ?? 0, y: ps.wrap_vertical ?? 0 };
  }

  return { x: ps.cut_horizontal, y: ps.cut_vertical };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), Math.max(min, max));
}

export interface PageBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface LayerBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface ClipInsets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

/**
 * The page the product actually ends up with: the sheet minus the margin the cutter takes off a spread
 * (or the cover wraps around the board). Print px, same space as the zone shapes.
 */
export function trimmedPageBox(ps: ZonePrintSizes): PageBox {
  const bleed = bleedOffset(ps);
  // The cut math (broken-geometry guard included) is the one print gets in `canvas_geometry.trim`.
  return { ...trimSheet(ps.width, ps.height, bleed.x, bleed.y) };
}

/**
 * The trimmed-off margin as a fraction of each sheet axis — lets a consumer that only has a rendered
 * sheet image (a server preview) show the printed page without knowing print px.
 */
export function trimFractions(ps: ZonePrintSizes): { x: number; y: number } {
  const page = trimmedPageBox(ps);
  return {
    x: ps.width > 0 ? page.x / ps.width : 0,
    y: ps.height > 0 ? page.y / ps.height : 0,
  };
}

/** The outer technical zone toggle — wrap on covers, bleed on spreads. */
export function outerZoneVisible(ps: ZonePrintSizes, vis?: ZoneVisibility | null): boolean {
  return visible(isCover(ps) ? vis?.wrap : vis?.bleed);
}

/**
 * The surface the editor presents: the whole pre-trim sheet while the technical zones are on (they are
 * what the margin is FOR), the printed page otherwise.
 */
export function editorSurfaceBox(ps: ZonePrintSizes, vis?: ZoneVisibility | null): PageBox {
  return outerZoneVisible(ps, vis) ? { x: 0, y: 0, width: ps.width, height: ps.height } : trimmedPageBox(ps);
}

/** Insets that clip a positioned layer down to the page; a layer already inside it is left untouched. */
export function pageClipInsets(layer: LayerBox, page: LayerBox): ClipInsets {
  return {
    top: Math.max(0, page.top - layer.top),
    right: Math.max(0, layer.left + layer.width - (page.left + page.width)),
    bottom: Math.max(0, layer.top + layer.height - (page.top + page.height)),
    left: Math.max(0, page.left - layer.left),
  };
}

function rect(x: number, y: number, width: number, height: number): ZoneRect {
  return { x, y, width, height, rotation: 0 };
}

function fillShape(id: string, r: ZoneRect, fill: string): ZoneShape {
  return { type: 'shape', id, rect: r, fill, blend: 'normal' };
}

function borderShape(id: string, r: ZoneRect, stroke: string, strokeWidth: number, dash: number[]): ZoneShape {
  return { type: 'shape', id, rect: r, stroke, strokeWidth, dash, blend: 'normal' };
}

/**
 * Builds the technical-zone shapes (bleed/wrap, safe frame, spine band, расстав bands) for a
 * spread/cover, in absolute print-px scene coordinates. Returns them in draw order (fills first,
 * borders last). Zones that do not fit (e.g. spine wider than the page) are omitted — mirrors the
 * `_fits` guards of the Konva layer.
 */
export function buildZoneShapes(ps: ZonePrintSizes, vis?: ZoneVisibility, zoneStyle: ZoneStyle = 'photobook'): ZoneShape[] {
  const shapes: ZoneShape[] = [];
  const cover = isCover(ps);
  const bleed = bleedOffset(ps);
  const bx = bleed.x;
  const by = bleed.y;
  const fullW = ps.width;
  const fullH = ps.height;

  const zoneFill = BLEED_ZONE_FILL_COLOR;
  const zoneStroke = BLEED_ZONE_STROKE_COLOR;

  const showOuter = outerZoneVisible(ps, vis);
  const showSafe = visible(vis?.safe);
  const showSpine = visible(vis?.spine);
  const showRasstav = visible(vis?.rasstav);

  // --- Bleed / wrap zone: four edge fills + dashed inner border ---
  if (showOuter) {
    if (bx > 0 || by > 0) {
      shapes.push(fillShape('zone-bleed-top', rect(0, 0, fullW, by), zoneFill));
      shapes.push(fillShape('zone-bleed-bottom', rect(0, fullH - by, fullW, by), zoneFill));
      shapes.push(fillShape('zone-bleed-left', rect(0, by, bx, fullH - by * 2), zoneFill));
      shapes.push(fillShape('zone-bleed-right', rect(fullW - bx, by, bx, fullH - by * 2), zoneFill));
    }

    shapes.push(
      borderShape(
        'zone-bleed-border',
        rect(bx, by, fullW - bx * 2, fullH - by * 2),
        zoneStroke,
        ZONE_BORDER_DEVICE_PX,
        ZONE_BORDER_DASH,
      ),
    );
  }

  // --- Cover spine band (centered vertical band) ---
  const spineWidth = cover ? (ps.spine_width ?? 0) : 0;
  const spineFits = spineWidth > 0 && spineWidth < fullW;
  if (spineFits && showSpine) {
    const spineRect = rect(fullW / 2 - spineWidth / 2, 0, spineWidth, fullH);
    shapes.push(fillShape('zone-spine-fill', spineRect, SPINE_ZONE_FILL_COLOR));
    shapes.push(borderShape('zone-spine-border', spineRect, SPINE_ZONE_STROKE_COLOR, ZONE_BORDER_DEVICE_PX, ZONE_BORDER_DASH));
  }

  // --- Расстав bands flanking the spine (cover only) ---
  if (cover && showRasstav) {
    const visibleWidth = fullW - 2 * bx;
    const leftPageWidth = Math.max(0, (visibleWidth - spineWidth) / 2);
    const rasstavInput = ps.rasstav_horizontal ?? ps.safe_horizontal;
    const rasstav = clamp(rasstavInput, 0, leftPageWidth);
    const trimTop = by;
    const trimBottom = fullH - by;
    const spineLeft = bx + leftPageWidth;
    const spineRight = fullW - bx - leftPageWidth;
    const height = trimBottom - trimTop;
    if (rasstav > 0 && height > 0) {
      const leftRect = rect(spineLeft - rasstav, trimTop, rasstav, height);
      const rightRect = rect(spineRight, trimTop, rasstav, height);
      shapes.push(fillShape('zone-rasstav-left', leftRect, RASSTAV_ZONE_FILL_COLOR));
      shapes.push(fillShape('zone-rasstav-right', rightRect, RASSTAV_ZONE_FILL_COLOR));
      // One frame around both bands and the spine: a border per band would share its vertical sides with
      // the spine border, and two dashes running in opposite directions fill each other's gaps into a solid line.
      const rasstavRect = rect(spineLeft - rasstav, trimTop, 2 * rasstav + spineWidth, height);
      shapes.push(
        borderShape('zone-rasstav-border', rasstavRect, RASSTAV_ZONE_STROKE_COLOR, ZONE_BORDER_DEVICE_PX, ZONE_BORDER_DASH),
      );
    }
  }

  // --- Safe frame: four inner-edge fills + dashed border, inset by safe_* from the trim ---
  if (showSafe) {
    if (zoneStyle === 'designEditor') {
      // Safe inset references the fold edge: wrap on covers, cut otherwise (mirrors DesignEditorZoneLayer refH/refV).
      const refH = bx > 0 ? bx : ps.cut_horizontal;
      const refV = by > 0 ? by : ps.cut_vertical;
      pushSafePages(shapes, ps, refH, refV, fullW, fullH, spineWidth);
    } else {
      pushSafeFrame(shapes, ps, bx, by, fullW, fullH);
    }
  }

  return shapes;
}

/** Photobook safe zone: one dashed frame spanning the whole trim, inset by safe_*. */
function pushSafeFrame(shapes: ZoneShape[], ps: ZonePrintSizes, bx: number, by: number, fullW: number, fullH: number): void {
  const left = bx;
  const top = by;
  const right = fullW - bx;
  const bottom = fullH - by;
  const w = right - left;
  const h = bottom - top;
  const safeH = ps.safe_horizontal;
  const safeV = ps.safe_vertical;
  if (w - safeH * 2 <= 0 || h - safeV * 2 <= 0) return;
  shapes.push(fillShape('zone-safe-top', rect(left, top, w, safeV), SAFE_ZONE_FILL_COLOR));
  shapes.push(fillShape('zone-safe-bottom', rect(left, bottom - safeV, w, safeV), SAFE_ZONE_FILL_COLOR));
  shapes.push(fillShape('zone-safe-left', rect(left, top + safeV, safeH, h - safeV * 2), SAFE_ZONE_FILL_COLOR));
  shapes.push(fillShape('zone-safe-right', rect(right - safeH, top + safeV, safeH, h - safeV * 2), SAFE_ZONE_FILL_COLOR));
  shapes.push(
    borderShape(
      'zone-safe-border',
      rect(left + safeH, top + safeV, w - safeH * 2, h - safeV * 2),
      SAFE_ZONE_STROKE_COLOR,
      ZONE_BORDER_DEVICE_PX,
      ZONE_BORDER_DASH,
    ),
  );
}

// Design-editor safe zone: one dashed frame per page, split at the spine center (fold center on a spread).
function pushSafePages(
  shapes: ZoneShape[],
  ps: ZonePrintSizes,
  refH: number,
  refV: number,
  fullW: number,
  fullH: number,
  spineWidth: number,
): void {
  const safeH = ps.safe_horizontal;
  const safeV = ps.safe_vertical;
  const centerX = fullW / 2;
  const half = spineWidth / 2;
  const top = refV;
  const bottom = fullH - refV;
  const pageH = bottom - top;
  const pageBounds: [number, number][] = [
    [refH, centerX - half],
    [centerX + half, fullW - refH],
  ];
  pageBounds.forEach(([left, right], i) => {
    const pageW = right - left;
    if (pageW - safeH * 2 <= 0 || pageH - safeV * 2 <= 0) return;
    shapes.push(fillShape(`zone-safe-${i}-top`, rect(left, top, pageW, safeV), SAFE_ZONE_FILL_COLOR));
    shapes.push(fillShape(`zone-safe-${i}-bottom`, rect(left, bottom - safeV, pageW, safeV), SAFE_ZONE_FILL_COLOR));
    const sideH = pageH - safeV * 2;
    shapes.push(fillShape(`zone-safe-${i}-outer`, rect(left, top + safeV, safeH, sideH), SAFE_ZONE_FILL_COLOR));
    shapes.push(fillShape(`zone-safe-${i}-inner`, rect(right - safeH, top + safeV, safeH, sideH), SAFE_ZONE_FILL_COLOR));
    shapes.push(
      borderShape(
        `zone-safe-${i}-border`,
        rect(left + safeH, top + safeV, pageW - safeH * 2, pageH - safeV * 2),
        SAFE_ZONE_STROKE_COLOR,
        ZONE_BORDER_DEVICE_PX,
        ZONE_BORDER_DASH,
      ),
    );
  });
}
